import React, { useState, useEffect, useCallback, useRef, useId } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, List, FolderSimple, Trash } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { queryKeys } from '../queries/keys';
import Sidebar from '../components/Sidebar';
import Toast from '../components/Toast';
import EmptyState from '../components/EmptyState';
import { CardGridSkeleton } from '../components/Skeleton';
import Modal, { CONFIRM_PANEL_MOTION } from '../components/Modal';

export default function MyCollections() {
  const { user, logout } = useAuth();
  const confirmDeleteTitleId = useId();
  const queryClient = useQueryClient();
  const collectionsKey = queryKeys.myCollections(user?.id);
  const [toast, setToast] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const toastTimeoutRef = useRef(null);

  const showToast = useCallback((message, type = 'info') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ message, type });
    toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  // Cache-first: a revisit to this page renders instantly from the cached
  // list (no skeleton) while react-query quietly revalidates in the
  // background, and refetches automatically when the tab regains focus.
  // enabled: !!user?.id is a second, query-level guard against ever firing
  // under the shared 'anon' key namespace (see queries/keys.js) — belt and
  // braces alongside ProtectedRoute never mounting this page pre-auth.
  const { data, isLoading: loading, isError } = useQuery({
    queryKey: collectionsKey,
    queryFn: () => api.getMyCollections(),
    enabled: !!user?.id,
  });
  const collections = data?.collections ?? [];

  useEffect(() => {
    if (isError) showToast('Failed to load collections', 'error');
  }, [isError, showToast]);

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  // Escape-to-close is handled by the shared Modal primitive (COL-89) —
  // no per-page keydown listener needed here anymore.
  const createMutation = useMutation({
    mutationFn: (payload) => api.createCollection(payload),
    onSuccess: (collection) => {
      queryClient.setQueryData(collectionsKey, (old) => ({
        ...old,
        collections: [collection, ...(old?.collections ?? [])],
      }));
      setCreateName('');
      setCreateDescription('');
      setShowCreateForm(false);
      showToast('Collection created', 'success');
    },
    onError: () => showToast('Failed to create collection', 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: (collectionId) => api.deleteCollection(collectionId),
    onSuccess: (_, collectionId) => {
      queryClient.setQueryData(collectionsKey, (old) => ({
        ...old,
        collections: (old?.collections ?? []).filter(c => c.id !== collectionId),
      }));
      setConfirmDelete(null);
      showToast('Collection deleted', 'success');
    },
    onError: () => showToast('Failed to delete collection', 'error'),
  });

  const handleCreate = (e) => {
    e.preventDefault();
    if (!createName.trim()) return;
    createMutation.mutate({
      name: createName.trim(),
      description: createDescription.trim() || undefined,
    });
  };

  const handleDelete = (collectionId) => {
    if (deleteMutation.isPending) return;
    deleteMutation.mutate(collectionId);
  };

  const creating = createMutation.isPending;
  const isDeleting = deleteMutation.isPending;

  return (
    <div className="app-layout">
      <Sidebar
        user={user}
        onLogout={logout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collectionsConfig={{ collections, activeCollectionId: null }}
      />

      <main className="main-content">
        <header className="content-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
            <button
              className="btn btn-ghost btn-icon sidebar-toggle"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation"
            >
              <MenuIcon />
            </button>
            <h1 className="page-title">My Collections</h1>
          </div>

          <div className="header-actions">
            <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
              <PlusIcon />
              <span>New Collection</span>
            </button>
          </div>
        </header>

        <div className="content-body">
          {/* Create collection form */}
          <AnimatePresence>
            {showCreateForm && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="collection-create-form"
              >
                <form onSubmit={handleCreate}>
                  <input
                    type="text"
                    placeholder="Collection name"
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    maxLength={100}
                    autoFocus
                    className="collection-input"
                  />
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    value={createDescription}
                    onChange={(e) => setCreateDescription(e.target.value)}
                    className="collection-input"
                  />
                  <div className="collection-create-actions">
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => { setShowCreateForm(false); setCreateName(''); setCreateDescription(''); }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={!createName.trim() || creating}
                    >
                      {creating ? 'Creating...' : 'Create'}
                    </button>
                  </div>
                </form>
              </motion.div>
            )}
          </AnimatePresence>

          {loading ? (
            <CardGridSkeleton minWidth="260px" />
          ) : collections.length === 0 ? (
            <EmptyState
              title="No collections yet"
              description="Create collections to organize your saved places, items, and more."
              action={
                <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
                  <PlusIcon />
                  Create your first collection
                </button>
              }
            />
          ) : (
            <motion.div
              className="collections-grid"
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.03 } } }}
            >
              {collections.map((collection) => (
                <motion.div
                  key={collection.id}
                  className="collection-card"
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 },
                  }}
                >
                  <div className="collection-card-thumbnails">
                    {collection.preview_thumbnails?.length > 0 ? (
                      collection.preview_thumbnails.slice(0, 4).map((url, i) => (
                        <div key={i} className="collection-card-thumb">
                          <img
                            src={url}
                            alt=""
                            loading="lazy"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      ))
                    ) : (
                      <div className="collection-card-empty-thumb">
                        <FolderIcon />
                      </div>
                    )}
                  </div>
                  <div className="collection-card-info">
                    <h3 className="collection-card-name">
                      {/* The whole card is clickable via ::after stretching this
                          link over .collection-card (position: relative) — see
                          .collection-card-link in index.css. Nesting the link
                          inside the heading, rather than wrapping the whole card
                          in a <button>/<a>, is what keeps the delete button below
                          from ending up nested inside another interactive
                          element (COL-91). */}
                      <Link to={`/collections/${collection.id}`} className="collection-card-link">
                        {collection.name}
                      </Link>
                    </h3>
                    <span className="collection-card-count">
                      {collection.item_count} {collection.item_count === 1 ? 'item' : 'items'}
                    </span>
                  </div>
                  <button
                    className="collection-card-delete"
                    onClick={() => setConfirmDelete(collection.id)}
                    title="Delete collection"
                    aria-label={`Delete ${collection.name}`}
                  >
                    <TrashIcon />
                  </button>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>

        {/* Delete confirmation dialog */}
        <AnimatePresence>
          {confirmDelete && (
            <Modal
              onClose={() => setConfirmDelete(null)}
              labelledBy={confirmDeleteTitleId}
              className="confirm-dialog"
              panelMotionProps={CONFIRM_PANEL_MOTION}
            >
                <h3 id={confirmDeleteTitleId}>Delete "{collections.find(c => c.id === confirmDelete)?.name}"?</h3>
                <p>This won't delete any saved items. They'll still appear in your library.</p>
                <div className="confirm-dialog-actions">
                  <button className="btn btn-ghost" onClick={() => setConfirmDelete(null)} disabled={isDeleting}>Cancel</button>
                  <button className="btn btn-danger" onClick={() => handleDelete(confirmDelete)} disabled={isDeleting}>{isDeleting ? 'Deleting...' : 'Delete'}</button>
                </div>
            </Modal>
          )}
        </AnimatePresence>
      </main>

      <Toast toast={toast} />
    </div>
  );
}

function PlusIcon() {
  return <Plus size={16} />;
}

function MenuIcon() {
  return <List size={20} />;
}

function FolderIcon() {
  return <FolderSimple size={32} opacity="0.3" />;
}

function TrashIcon() {
  return <Trash size={14} />;
}
